/**
 * Course conclusion unit renderer.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";

import { getCorrectProblemBlockCount, getGradeForBlock } from "utils/grade.util";
import ConclusionGuide from "./ConclusionGuide";
import ConclusionResult from "./ConclusionResult";
import ProblemSurvey from "pages/board/problems/ProblemSurvey";
import { getQAArray } from "utils/course";
import { useAsterController } from "context";
import { actionSendSurveyEmail } from "context/action";
import { getBrighterColor } from "utils/string";

// import { actionSendSurveyEmail } from "pages/redux/actions/app.action";

/**
 * Orchestrates conclusion survey, guide, and result screens.
 *
 * @param {{ xblocks?: Array }} props
 */
const ConclusionRender = (props) => {
  
  const { xblocks, onSubmitProblem } = props;

  const { t } = useTranslation('common');

  const [controller, dispatch] = useAsterController();
  const { courses, themes } = controller;

  const colorMain1 = (themes || []).find(v => v.name === 'color_main1')?.value || '#F76060';
  const { courseId } = useParams();
  const currCourse = useMemo(() => (courses.data || []).find(v => v.id === courseId) || {}, [courses, courseId]);

  const [cursor, setCursor] = useState(0);
  const [problemBlocks, setProblemBlocks] = useState([]);
  const [otherBlocks, setOtherBlocks] = useState([]);
  const [correctCount, setCorrectCount] = useState(null);

  const [qaArray, setQAArray] = useState([]);
  const [surveyRes, setSurveyRes] = useState([]);

  useEffect(() => {
    const problems = xblocks.filter(b => b.type === 'problem');
    setProblemBlocks(problems);
    setOtherBlocks(xblocks.filter(b => b.type !== 'problem'));

    setQAArray(getQAArray(problems));
  }, [xblocks]);

  useEffect(() => {
    if (problemBlocks && problemBlocks.length) {
      const gradeCounter = getCorrectProblemBlockCount(currCourse, problemBlocks.map(p => p.id));
      if (gradeCounter['haveGraded']) {
        setCorrectCount(gradeCounter['counter']);
      }
    }
  }, [problemBlocks, currCourse]);

  // If continue button clicked
  /**
   * Advances the conclusion flow after the survey.
   *
   * @returns {void}
   */
  const onContinueClick = () => {
    // If it reached to final question
    if (cursor + 1 === problemBlocks.length) {
      setCorrectCount(getCorrectProblemBlockCount(currCourse, problemBlocks.map(p => p.id))['counter']);

      // Sending email to admin
      actionSendSurveyEmail(dispatch, surveyRes);
    }
    setCursor(cursor + 1);
  };

  /**
   * Captures a conclusion-survey answer.
   *
   * @param {Object} payload Answer payload.
   * @returns {void}
   */
  const onMockProblemSubmit = (payload) => {

    // Now update survey result
    if (payload.choices && payload.choices.length) {
      const choiceId = payload.choices[0].id;
      const choiceAns = payload.choices[0].student_answer;
      const qaItem = qaArray.find(v => choiceId.includes(v.id));
      if (qaItem) {
        const option = qaItem.answers.find(v => v.option === choiceAns);
        setSurveyRes([...surveyRes, { question: qaItem.question, answer: option.text }]);
      }
    }

    onSubmitProblem(payload, 'ALLOW_POST');
  };

  return <>
    <ConclusionGuide xblocks={otherBlocks} />
    {
      correctCount === null && <>
        {
          0 <= cursor && cursor < problemBlocks.length && <>
            <div className="text-xs mt-4 text-uppercase">
              {t('Question')}&nbsp;
              {cursor + 1}/{problemBlocks.length}
            </div>
            <div className="flex mt-2 bg-gray-200 w-full rounded mb-4" style={{ height: 10 }}>
              <div style={{ width: `${(cursor + 1) * 100 / problemBlocks.length}%`, backgroundImage: `linear-gradient(to right, ${getBrighterColor(colorMain1, 50)}, ${colorMain1})` }} className="rounded"></div>
            </div>
            <ProblemSurvey
              key={cursor}
              rawText={problemBlocks[cursor].html}
              grade={getGradeForBlock(currCourse, problemBlocks[cursor].id)}
              courseId={courseId}
              blockId={problemBlocks[cursor].id}
              onSubmitProblem={onMockProblemSubmit}
              onContinueClick={onContinueClick}
            />
          </>
        }
      </>
    }

    {
      correctCount !== null && <>
        <ConclusionResult />
      </>
    }

  </>;
};

export default ConclusionRender;